"use client";

import { useEffect, useRef } from "react";

import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { Logger } from "@/utils/logger";
import { meshTokenStore } from "@/utils/mesh-token-storage";

import { buildTransferReport, type TransferReportPayload } from "./build-transfer-report";
import type { TransferProgressData, TransferStep } from "./transfer-progress-card";
import { useTransferProgress } from "./use-transfer-progress";

// A transfer's funds have landed once it reaches one of these steps. "available"
// covers deposits and same-chain/queued withdrawals; "completed" covers cross-chain
// withdrawals. Cancelled/in-progress steps are intentionally excluded.
//
// This answers "have the funds landed". `deriveCapabilities(...).isTerminal` answers a different
// question — "is there anything left to do" — and the two are *supposed* to disagree. A sweepable
// cross-chain deposit at "available" has landed (report it) and is not terminal (the sweep is
// still offered). Unifying them means every sweepable deposit a user never sweeps is never POSTed:
// permanent funding-attribution loss, invisible in the UI. See `use-report-confirmed-transfers.test.ts`
// for the pin.
export const TERMINAL_STEPS: ReadonlySet<TransferStep> = new Set<TransferStep>([
  "available",
  "completed",
]);

/** POST a confirmed transfer to the warehouse via our own server route. Resolves
 *  true on a 2xx, false on network error or non-2xx — the caller only persists
 *  the "reported" flag on success, so a failure is retried on the next provider
 *  mount. Goes through `/api/transfers` (a server-side hop) rather than the
 *  trading API directly: the trading endpoint is internal-only and requires the
 *  gateway-injected `x-aptos-identifier`, which the browser cannot produce. */
async function postTransferReport(payload: TransferReportPayload): Promise<boolean> {
  try {
    const res = await fetch("/api/transfers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Transfer report failed: HTTP ${res.status}`);
    return true;
  } catch (error: unknown) {
    Logger.error(error, { message: "Failed to record user transfer" });
    return false;
  }
}

type FunDepositTransfer = Extract<TransferProgressData, { type: "fun-deposit" }>;

function reportsFirst(a: FunDepositTransfer, b: FunDepositTransfer): boolean {
  if (a.projection.isDeliveryOwner !== b.projection.isDeliveryOwner) {
    return a.projection.isDeliveryOwner;
  }
  return a.id < b.id;
}

/** The fun cards sharing this card's sweep, this card included; none for any other card. */
function sweepCards(
  transfer: TransferProgressData,
  transfers: readonly TransferProgressData[],
): FunDepositTransfer[] {
  if (transfer.type !== "fun-deposit" || !transfer.deliveryId) return [];
  return transfers.filter(
    (t): t is FunDepositTransfer =>
      t.type === "fun-deposit" && t.deliveryId === transfer.deliveryId,
  );
}

/**
 * A sweep backing several Fun cards reports once: from its owner's card, else from the smallest
 * card id, and not at all once one of its cards has reported.
 */
function sweepReportedElsewhere(
  transfer: TransferProgressData,
  transfers: readonly TransferProgressData[],
): boolean {
  const cards = sweepCards(transfer, transfers);
  if (cards.length === 0) return false;
  if (cards.some((card) => card.reportedToWarehouse)) return true;
  const reporter = cards.reduce((best, card) => (reportsFirst(card, best) ? card : best));
  return reporter.id !== transfer.id;
}

/**
 * A delivery's NEAR and Fun cards share one key, so an in-flight report blocks the other card
 * even once the reporting card is replaced or dismissed.
 */
function reportKey(transfer: TransferProgressData): string {
  return (transfer.type === "fun-deposit" || transfer.type === "xchain-deposit") &&
    transfer.deliveryId
    ? `sweep:${transfer.deliveryId}`
    : transfer.id;
}

/**
 * Provider-level effect that records each confirmed transfer in the warehouse
 * exactly once, for funding-attribution analytics. Centralized here so every
 * transfer method (mesh, bridge, queued) is captured at a single chokepoint,
 * regardless of which confirmation path advanced it to a terminal step.
 */
export function useReportConfirmedTransfers() {
  const { transfers, updateTransfer } = useTransferProgress();
  const { wallet, accountAddress } = useDecibelWallet();
  const walletProvider = wallet?.name;
  // Guards against a second POST in the render before the localStorage-backed
  // `reportedToWarehouse` flag propagates back through `transfers`. This is a
  // per-mount Set, so a POST in flight when the provider unmounts can re-POST on
  // the next mount — that's safe because the user_transfers table dedups on
  // (account, transfer_id) via ReplacingMergeTree, so don't add a cross-mount
  // lock here.
  const postedRef = useRef<Set<string>>(new Set());
  const transfersRef = useRef(transfers);

  useEffect(() => {
    transfersRef.current = transfers;
    const justConfirmed = transfers.filter(
      (t) =>
        !t.reportedToWarehouse &&
        !postedRef.current.has(reportKey(t)) &&
        TERMINAL_STEPS.has(t.currentStep) &&
        !sweepReportedElsewhere(t, transfers),
    );

    for (const transfer of justConfirmed) {
      // Best-effort broker attribution: the most recently stored Mesh token. If a
      // user connected multiple brokers, [0] may not be the one used for this
      // deposit — acceptable for attribution, and only relevant for mesh deposits.
      const broker =
        transfer.type === "mesh-deposit" && accountAddress
          ? meshTokenStore(accountAddress).get()[0]?.brokerName
          : undefined;

      const payload = buildTransferReport(transfer, { walletProvider, broker });

      // Transfers persisted before `subaccountAddress` was added to the withdraw
      // variants have no account to attribute, so `account` is empty. Skip them:
      // the server requires a non-empty account and would 400, which releases the
      // guard below and retries on every `transfers` change — a failed-POST/error
      // loop until the user manually dismisses the card.
      if (!payload.account) continue;

      const key = reportKey(transfer);
      postedRef.current.add(key);

      void postTransferReport(payload).then((ok) => {
        if (ok) {
          // Persist the durable guard so we don't re-POST across mounts. Every card holding the
          // key takes it, read when the POST lands: the sender may have been replaced meanwhile.
          for (const card of transfersRef.current) {
            if (reportKey(card) === key) updateTransfer(card.id, { reportedToWarehouse: true });
          }
        } else {
          // Release the in-session guard so the next provider mount retries.
          postedRef.current.delete(key);
        }
      });
    }
  }, [transfers, accountAddress, walletProvider, updateTransfer]);
}
